import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import api, { fmtRM, fmtDate } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { ArrowLeft, Plus, Trash2, BedDouble } from "lucide-react";

const VISA_STYLE = {
  not_submitted: "bg-slate-100 text-slate-600 border-slate-300",
  submitted: "bg-blue-50 text-blue-700 border-blue-200",
  processing: "bg-indigo-50 text-indigo-700 border-indigo-200",
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  issued: "bg-green-100 text-green-800 border-green-300",
  rejected: "bg-red-50 text-red-700 border-red-200",
};

const CAPACITY = { Quad: 4, Triple: 3, Double: 2, Single: 1, Family: 6 };

export default function DepartureDetail() {
  const { departureId } = useParams();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ room_type: "Quad", hotel: "", occupants: [] });

  const load = useCallback(() => {
    api.get(`/departures/${departureId}/detail`).then((r) => setData(r.data)).catch(() => toast.error("Failed to load"));
  }, [departureId]);
  useEffect(load, [load]);

  const toggleOccupant = (name) => {
    setForm((f) => {
      const has = f.occupants.includes(name);
      const next = has ? f.occupants.filter((o) => o !== name) : [...f.occupants, name];
      if (next.length > CAPACITY[f.room_type]) {
        toast.error(`${f.room_type} fits max ${CAPACITY[f.room_type]}`);
        return f;
      }
      return { ...f, occupants: next };
    });
  };

  const createRoom = async (e) => {
    e.preventDefault();
    try {
      await api.post("/rooms", { departure_id: departureId, ...form });
      toast.success("Room created");
      setOpen(false);
      setForm({ room_type: "Quad", hotel: "", occupants: [] });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const removeRoom = async (roomId) => {
    try {
      await api.delete(`/rooms/${roomId}`);
      load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  if (!data) return <div className="flex justify-center h-64 items-center" data-testid="departure-detail-loading"><div className="w-8 h-8 border-4 border-brand-800 border-t-gold-500 rounded-full animate-spin" /></div>;

  const { departure: d, package: pkg, bookings, rooms, readiness, pnl } = data;
  const assignedNames = new Set(rooms.flatMap((r) => r.occupants || []));

  return (
    <div className="space-y-5" data-testid="departure-detail-page">
      <Link to="/app/departures" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-emerald-800 transition-colors" data-testid="back-to-departures">
        <ArrowLeft className="w-4 h-4" />Back to Departures
      </Link>

      <div className="pro-card p-6" data-testid="departure-header">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground">{pkg?.package_code} • {pkg?.category?.replace(/_/g, " ")}</div>
            <h1 className="font-sans text-2xl font-bold text-slate-900 mt-1">{pkg?.name}</h1>
            <div className="text-sm text-muted-foreground mt-1">
              {fmtDate(d.date)} → {fmtDate(d.return_date)} • {d.flight || "TBA"} • Mutawif: {d.mutawif || "—"} • TL: {d.tour_leader || "—"}
            </div>
          </div>
          <div className="flex gap-4 text-center">
            {[["Revenue", fmtRM(pnl.revenue)], ["Cost", fmtRM(pnl.cost)], ["Profit", fmtRM(pnl.profit)], ["Margin", `${pnl.margin}%`]].map(([l, v]) => (
              <div key={l} className="bg-sand rounded-xl px-4 py-2.5" data-testid={`pnl-${l.toLowerCase()}`}>
                <div className="font-sans text-sm font-bold text-slate-900 tabular-nums">{v}</div>
                <div className="text-[9px] font-mono uppercase tracking-wider text-muted-foreground">{l}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="readiness-cards">
        {[["Payment", readiness.payment], ["Visa", readiness.visa], ["Rooming", readiness.rooming], ["Overall Ready", readiness.overall]].map(([l, v]) => (
          <div key={l} className={`pro-card p-4 ${l === "Overall Ready" ? "border-gold-500/40 bg-gold-100/40" : ""}`} data-testid={`readiness-${l.toLowerCase().replace(/\s/g, "-")}`}>
            <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{l}</div>
            <div className={`font-sans text-2xl font-bold tabular-nums mt-1 ${v >= 90 ? "text-emerald-700" : v >= 60 ? "text-gold-700" : "text-destructive"}`}>{v}%</div>
            <Progress value={v} className="h-1.5 mt-2" />
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 pro-card overflow-x-auto" data-testid="departure-jemaah">
          <h2 className="font-sans font-semibold text-slate-900 p-5 pb-3">Jemaah Manifest ({bookings.length} bookings)</h2>
          <table className="w-full text-sm min-w-[600px]">
            <thead>
              <tr className="border-y border-sand-border text-left bg-[#F8F6F2]/80">
                {["Customer", "Pax", "Room", "Payment", "Visa"].map((h) => (
                  <th key={h} className="px-5 py-2.5 text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {bookings.map((b) => {
                const pct = b.total > 0 ? Math.round((b.paid / b.total) * 100) : 0;
                return (
                  <tr key={b.booking_id} className="border-b border-sand-border/60" data-testid={`manifest-${b.booking_no}`}>
                    <td className="px-5 py-3 font-medium text-slate-900">{b.customer_name}</td>
                    <td className="px-5 py-3 font-mono text-xs">{b.pax}</td>
                    <td className="px-5 py-3 text-xs">{b.room_type}</td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        <Progress value={pct} className="h-1.5 w-16" />
                        <span className="text-[10px] font-mono text-muted-foreground tabular-nums">{pct}%</span>
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full border text-[10px] font-mono uppercase ${VISA_STYLE[b.visa_status] || VISA_STYLE.not_submitted}`}>
                        {(b.visa_status || "not_submitted").replace(/_/g, " ")}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {bookings.length === 0 && <div className="text-center py-10 text-muted-foreground text-sm">No jemaah yet</div>}
        </div>

        <div className="pro-card p-5" data-testid="rooming-panel">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-sans font-semibold text-slate-900">Rooming ({rooms.length})</h2>
            <Button size="sm" data-testid="add-room-button" onClick={() => setOpen(true)} className="h-8 rounded-full bg-brand-800 hover:bg-brand-700 text-sand text-xs">
              <Plus className="w-3.5 h-3.5 mr-1" />Add Room
            </Button>
          </div>
          <div className="space-y-3 max-h-[480px] overflow-y-auto">
            {rooms.map((r) => (
              <div key={r.room_id} className="border border-sand-border rounded-xl p-3" data-testid={`room-card-${r.room_no}`}>
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-1.5 text-sm font-medium text-slate-900">
                    <BedDouble className="w-4 h-4 text-gold-600" />{r.room_no} • {r.room_type}
                  </span>
                  <button data-testid={`delete-room-${r.room_no}`} onClick={() => removeRoom(r.room_id)} className="text-slate-300 hover:text-destructive transition-colors">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
                {r.hotel && <div className="text-[10px] text-muted-foreground mt-0.5">{r.hotel}</div>}
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {(r.occupants || []).map((o) => (
                    <span key={o} className="px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-100 text-[10px] text-emerald-800">{o}</span>
                  ))}
                  {(r.occupants || []).length === 0 && <span className="text-[10px] text-muted-foreground">Empty</span>}
                </div>
              </div>
            ))}
            {rooms.length === 0 && <div className="text-center py-8 text-muted-foreground text-xs">No rooms assigned yet</div>}
          </div>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="add-room-dialog" className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-sans font-semibold text-lg">Add Room</DialogTitle></DialogHeader>
          <form onSubmit={createRoom} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Room Type</Label>
                <Select value={form.room_type} onValueChange={(v) => setForm({ ...form, room_type: v, occupants: [] })}>
                  <SelectTrigger data-testid="room-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.keys(CAPACITY).map((r) => <SelectItem key={r} value={r}>{r} (max {CAPACITY[r]})</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Hotel</Label>
                <Input data-testid="room-hotel-input" value={form.hotel} onChange={(e) => setForm({ ...form, hotel: e.target.value })} placeholder={pkg?.hotel_makkah || "Hotel"} />
              </div>
            </div>
            <div>
              <Label>Occupants ({form.occupants.length}/{CAPACITY[form.room_type]})</Label>
              <div className="mt-2 border border-sand-border rounded-xl max-h-48 overflow-y-auto p-2 space-y-1" data-testid="room-occupants-list">
                {bookings.filter((b) => !assignedNames.has(b.customer_name)).map((b) => (
                  <label key={b.booking_id} className="flex items-center gap-2.5 px-2 py-1.5 rounded-lg hover:bg-sand cursor-pointer text-sm">
                    <input type="checkbox" data-testid={`occupant-${b.booking_no}`} checked={form.occupants.includes(b.customer_name)}
                      onChange={() => toggleOccupant(b.customer_name)} className="accent-emerald-800" />
                    <span>{b.customer_name}</span>
                    <span className="text-[10px] text-muted-foreground font-mono ml-auto">{b.pax} pax</span>
                  </label>
                ))}
              </div>
            </div>
            <Button data-testid="room-submit-button" type="submit" className="w-full bg-brand-800 hover:bg-brand-700 text-sand rounded-full">Create Room</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
